// CalendarPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import Sidebar from "../components/Sidebar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { startOfWeek as dStartOfWeek, endOfWeek } from "date-fns";
import { de } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";

import EventOverlay from "../components/EventOverlay";
import ImportPreviewOverlay from "../components/ImportPreviewOverlay";
import FileDropZone from "../components/FileDropZone";

const locales = { de };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const calendarMessages = {
  date: "Datum",
  time: "Zeit",
  event: "Termin",
  allDay: "Ganztägig",
  week: "Woche",
  work_week: "Arbeitswoche",
  day: "Tag",
  month: "Monat",
  previous: "Zurück",
  next: "Weiter",
  yesterday: "Gestern",
  tomorrow: "Morgen",
  today: "Heute",
  agenda: "Agenda",
  noEventsInRange: "In diesem Zeitraum gibt es keine Termine.",
  showMore: (total) => `+${total} weitere`,
};

const API_BASE = process.env.REACT_APP_API_URL;

export default function CalendarPage() {
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState("month");

  // DB master events (UI-shaped) — used for editing flow
  const [events, setEvents] = useState([]);

  // Pre-expanded occurrences from backend — used for calendar display
  const [displayEvents, setDisplayEvents] = useState([]);

  // Overlay state
  const [showOverlay, setShowOverlay] = useState(false);
  const [mode, setMode] = useState("create"); // "create" | "edit"

  // IMPORTANT FIX: store the clicked event object directly
  const [selectedEventObj, setSelectedEventObj] = useState(null);
  const [clickedOccurrenceStart, setClickedOccurrenceStart] = useState(null);

  // Import UI
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");

  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importStartDate, setImportStartDate] = useState(() => {
    const monday = dStartOfWeek(new Date(), { weekStartsOn: 1 });
    return format(monday, "yyyy-MM-dd");
  });

  // Import preview flow
  const [showImportPreview, setShowImportPreview] = useState(false);
  // API-shaped payloads from extractor (NOT yet saved to DB)
  const [importPreviewEvents, setImportPreviewEvents] = useState([]);
  const [editingImportIndex, setEditingImportIndex] = useState(null);

  // ---- Load events on mount ----
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/events`, { credentials: "include" });
        if (!res.ok) throw new Error(`GET /events failed: ${res.status}`);
        const list = await res.json();
        setEvents(list.map(fromApiEvent));
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // ---- Visible range handling ----
  const [range, setRange] = useState(() => getViewRange(date, view));
  useEffect(() => {
    setRange(getViewRange(date, view));
  }, [date, view]);

  function handleRangeChange(r) {
    if (Array.isArray(r) && r.length) setRange({ start: r[0], end: r[r.length - 1] });
    else if (r?.start && r?.end) setRange({ start: r.start, end: r.end });
    else setRange(getViewRange(date, view));
  }

  // Fetch pre-expanded occurrences from backend whenever visible range changes
  useEffect(() => {
    const startStr = range.start.toISOString().slice(0, 10);
    const endStr = range.end.toISOString().slice(0, 10);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/events/occurrences?start=${startStr}&end=${endStr}`,
          { credentials: "include" },
        );
        if (cancelled) return;
        if (!res.ok) return;
        const list = await res.json();
        if (!cancelled) setDisplayEvents(list.map(fromApiEvent));
      } catch {
        // ignore — display simply stays empty
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  // Overlay initial event:
  // - If editing an imported preview event, build UI event from preview payload
  // - Else use selectedEventObj (clicked instance)
  const overlayInitialEvent = useMemo(() => {
    if (editingImportIndex !== null) {
      const api = importPreviewEvents[editingImportIndex];
      return api ? fromApiEvent(api) : null;
    }
    return selectedEventObj;
  }, [editingImportIndex, importPreviewEvents, selectedEventObj]);

  // ---- Create/Edit open ----
  function openCreate() {
    setMode("create");
    setSelectedEventObj(null);
    setClickedOccurrenceStart(null);
    setShowOverlay(true);
  }

  // IMPORTANT FIX: open edit using the event instance from RBC
  function openEdit(eventInstance) {
    setMode("edit");
    setSelectedEventObj(eventInstance);
    setClickedOccurrenceStart(eventInstance?.start || null);
    setShowOverlay(true);
  }

  // Edit an imported preview event (reuse EventOverlay)
  function openEditImported(index) {
    setEditingImportIndex(index);
    setMode("create"); // not a DB edit, we just reuse overlay form
    setSelectedEventObj(null);
    setClickedOccurrenceStart(null);

    // close preview to avoid stacking z-index / backdrop issues
    setShowImportPreview(false);
    setShowOverlay(true);
  }

  function handleCloseOverlay() {
    setShowOverlay(false);
    setSelectedEventObj(null);

    // if we were editing an imported preview event, return to preview overlay
    if (editingImportIndex !== null) {
      setEditingImportIndex(null);
      setShowImportPreview(true);
    }
  }

  // ---- Save flow ----
  async function handleSave(evt) {
    // If editing preview event, do not persist to DB
    if (editingImportIndex !== null) {
      const apiPayload = toApiEvent(evt);
      setImportPreviewEvents((prev) => {
        const next = [...prev];
        next[editingImportIndex] = apiPayload;
        return next;
      });

      setShowOverlay(false);
      setEditingImportIndex(null);
      setShowImportPreview(true);
      return;
    }

    const uiEvent = fromApiEvent(toApiEvent(evt));

    // Normal DB events save
    if (mode === "edit") {
      setEvents((prev) => prev.map((e) => (e.id === uiEvent.id ? uiEvent : e)));
    } else {
      setEvents((prev) => [...prev, uiEvent]);
    }
    setDate(uiEvent.start);
    setShowOverlay(false);

    try {
      if (mode === "edit") {
        await fetch(`${API_BASE}/events/${encodeURIComponent(evt.id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(toApiEvent(uiEvent)),
        });
      } else {
        const res = await fetch(`${API_BASE}/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(toApiEvent(uiEvent)),
        });
        if (!res.ok) throw new Error(`POST /events failed: ${res.status}`);
        const saved = await res.json();

        // merge canonical server response back in
        if (saved?.id && saved.id !== evt.id) {
          setEvents((prev) => prev.map((e) => (e.id === evt.id ? fromApiEvent(saved) : e)));
        }
      }
      // Refresh occurrences so the calendar reflects the latest state
      fetch(`${API_BASE}/events/occurrences?start=${range.start.toISOString().slice(0,10)}&end=${range.end.toISOString().slice(0,10)}`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).then((list) => setDisplayEvents(list.map(fromApiEvent))).catch(() => {});
    } catch (err) {
      console.error(err);
      // rollback by refetch
      try {
        const res = await fetch(`${API_BASE}/events`, { credentials: "include" });
        const list = await res.json();
        setEvents(list.map(fromApiEvent));
        fetch(`${API_BASE}/events/occurrences?start=${range.start.toISOString().slice(0,10)}&end=${range.end.toISOString().slice(0,10)}`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).then((list) => setDisplayEvents(list.map(fromApiEvent))).catch(() => {});
      } catch (e2) {
        console.error(e2);
      }
    }
  }

  async function handleDelete(id) {
    const snapshot = events;
    setEvents((prev) => prev.filter((e) => e.id !== id));
    setSelectedEventObj(null);
    setClickedOccurrenceStart(null);
    setShowOverlay(false);

    try {
      const res = await fetch(`${API_BASE}/events/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`DELETE /events/${id} failed: ${res.status}`);
      fetch(`${API_BASE}/events/occurrences?start=${range.start.toISOString().slice(0,10)}&end=${range.end.toISOString().slice(0,10)}`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).then((list) => setDisplayEvents(list.map(fromApiEvent))).catch(() => {});
    } catch (err) {
      console.error(err);
      setEvents(snapshot);
    }
  }

  // ---- Import dialog flow ----
  function openImportDialogFlow() {
    if (importing) return;
    const monday = dStartOfWeek(date, { weekStartsOn: 1 });
    setImportStartDate(format(monday, "yyyy-MM-dd"));
    setImportError("");
    setShowImportDialog(true);
  }

  function closeImportDialog() {
    if (importing) return;
    setShowImportDialog(false);
  }

  function validateImportDate() {
    if (importStartDate) return true;
    setImportError("Bitte zuerst das Startdatum auswählen.");
    return false;
  }

  // ---- Import route call ----
  async function importFile(file) {
    const form = new FormData();
    form.append("file", file);
    form.append("start_date", importStartDate);

    setImporting(true);
    try {
      const res = await fetch(`${API_BASE}/events/import-from-plan`, {
        method: "POST",
        credentials: "include",
        body: form,
      });

      //TODO remove block later
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        console.error("Import failed:", data);
        throw new Error(data?.error || data?.detail || `Import failed (${res.status})`);
      }
      //TODO remove block later
     
      //if (!res.ok) throw new Error(`Import failed: ${res.status}`); 
      //const data = await res.json();

      // Expect: { events: [ ...payloads compatible with POST /events ... ] }
      if (Array.isArray(data?.events)) {
        setImportPreviewEvents(data.events);
        setShowImportDialog(false);
        setShowImportPreview(true);
      } else {
        throw new Error("Der Kalender konnte nicht gelesen werden.");
      }
    } catch (err) {
      console.error(err);
      setImportError(err?.message || "Import fehlgeschlagen.");
    } finally {
      setImporting(false);
    }
  }

  // Commit imported events to DB (POST /events for each)
  async function commitImportedEvents() {
    if (importing) return;
    if (!Array.isArray(importPreviewEvents) || importPreviewEvents.length === 0) {
      setShowImportPreview(false);
      return;
    }

    setImporting(true);
    try {
      const created = [];
      for (const payload of importPreviewEvents) {
        const res = await fetch(`${API_BASE}/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`POST /events failed during commit: ${res.status}`);
        created.push(await res.json());
      }

      setEvents((prev) => mergeById(prev, created.map(fromApiEvent)));
      setShowImportPreview(false);
      setImportPreviewEvents([]);
      setEditingImportIndex(null);
      fetch(`${API_BASE}/events/occurrences?start=${range.start.toISOString().slice(0,10)}&end=${range.end.toISOString().slice(0,10)}`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).then((list) => setDisplayEvents(list.map(fromApiEvent))).catch(() => {});
    } catch (err) {
      console.error(err);
    } finally {
      setImporting(false);
    }
  }

  function cancelImportPreview() {
    if (importing) return;
    setShowImportPreview(false);
    setImportPreviewEvents([]);
    setEditingImportIndex(null);
  }

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar active="kalender" />
      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="w-full h-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          <div className="flex items-center justify-between mb-0">
            <h1 className="text-3xl font-semibold text-gray-900">Kalender</h1>
          </div>

          <div className="grid gap-[2rem]">
            <div className="flex items-center justify-between">
              <div className="text-xl font-semibold"></div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={openImportDialogFlow}
                  disabled={importing}
                  className="inline-flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3 text-lg font-semibold text-gray-900 shadow-sm hover:bg-white/70 disabled:opacity-60"
                >
                  {importing ? "Importiert..." : "Kalender importieren"}
                </button>

                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center gap-3 rounded-2xl bg-[#84bbff] px-4 py-3 text-white text-lg font-semibold shadow-md hover:bg-[#5d9ef7] active:scale-[.99]"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20">+</span>
                  Termin hinzufügen
                </button>

              </div>
            </div>

            <Calendar
              localizer={localizer}
              events={displayEvents}
              date={date}
              onNavigate={setDate}
              view={view}
              onView={setView}
              onRangeChange={handleRangeChange}
              culture="de"
              messages={calendarMessages}
              style={{ height: 700 }}
              onSelectEvent={(inst) => openEdit(inst)} // <-- IMPORTANT FIX
            />

            <EventOverlay
              open={showOverlay}
              mode={editingImportIndex !== null ? "create" : mode}
              initialDate={date}
              initialEvent={overlayInitialEvent}
              occurrenceStart={clickedOccurrenceStart}
              onClose={handleCloseOverlay}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </main>

      {/* Import date dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={closeImportDialog} />
          <div
            className="absolute left-1/2 top-24 w-[min(560px,92vw)] -translate-x-1/2 rounded-2xl border border-black/10 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Kalender importieren</div>
              <button
                type="button"
                onClick={closeImportDialog}
                disabled={importing}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Schließen
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="text-sm text-gray-700">
                Wähle das Datum aus, ab dem der importierte Kalender gültig ist (Start des Plans).
              </div>

              <div className="mt-3 flex items-center gap-3">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">Startdatum</label>
                <input
                  type="date"
                  value={importStartDate}
                  onChange={(e) => setImportStartDate(e.target.value)}
                  className="rounded-md border border-gray-200 px-3 py-2 text-sm"
                  disabled={importing}
                />
              </div>

              <FileDropZone
                onFile={importFile} onError={setImportError} error={importError}
                validate={validateImportDate} disabled={importing}
                accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff,.heic,.heif"
                formats="Bild oder PDF" busyText="Kalender wird verarbeitet…" className="mt-5"
              />

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeImportDialog}
                  disabled={importing}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Overlay */}
      <ImportPreviewOverlay
        open={showImportPreview}
        importing={importing}
        events={importPreviewEvents}
        onClose={cancelImportPreview}
        onEdit={openEditImported}
        onCommit={commitImportedEvents}
      />
    </div>
  );
}

/* ---------------- serialization helpers ---------------- */

function toApiEvent(evt) {
  const nm = evt.notification_minutes_before;
  const nmList = Array.isArray(nm) ? nm : typeof nm === "number" ? [nm] : [];

  return {
    id: evt.id,
    title: evt.title,
    description: evt.description || "",
    start: evt.start instanceof Date ? evt.start.toISOString() : evt.start,
    end: evt.end instanceof Date ? evt.end.toISOString() : evt.end,
    recurrence: evt.recurrence
      ? {
          ...evt.recurrence,
          end_date:
            evt.recurrence.end_date instanceof Date
              ? evt.recurrence.end_date.toISOString()
              : evt.recurrence.end_date ?? null,
        }
      : null,
    exclusions: Array.isArray(evt.exclusions) ? evt.exclusions : [],
    patients: evt.patients || { mode: "all", ids: [] },
    notification_minutes_before: nmList,
  };
}

function fromApiEvent(api) {
  const nm = api.notification_minutes_before;
  const nmList = Array.isArray(nm) ? nm : typeof nm === "number" ? [nm] : [];
  const start = api.start ? new Date(api.start) : new Date();
  const end = api.end ? new Date(api.end) : new Date();

  return {
    id: api.id,
    title: api.title,
    description: api.description ?? "",
    start,
    end,
    // Preserve series start/end for occurrences so delete operations
    // always send the series start to the backend, not the occurrence start.
    originalStart: api.originalStart ? new Date(api.originalStart) : start,
    originalEnd: api.originalEnd ? new Date(api.originalEnd) : end,
    recurrence: api.recurrence
      ? {
          ...api.recurrence,
          end_date: api.recurrence.end_date ? new Date(api.recurrence.end_date) : null,
        }
      : null,
    exclusions: Array.isArray(api.exclusions) ? api.exclusions : [],
    patients: api.patients || { mode: "all", ids: [] },
    notification_minutes_before: nmList,
  };
}


function mergeById(existing, incoming) {
  const map = new Map(existing.map((e) => [e.id, e]));
  for (const e of incoming) map.set(e.id, e);
  return Array.from(map.values());
}


/* ---------------- calendar helpers ---------------- */

function getViewRange(centerDate, viewName) {
  if (viewName === "month") {
    const start = dStartOfWeek(startOfMonth(centerDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(centerDate), { weekStartsOn: 1 });
    return { start, end };
  }
  if (viewName === "week") {
    const start = dStartOfWeek(centerDate, { weekStartsOn: 1 });
    const end = endOfWeek(centerDate, { weekStartsOn: 1 });
    return { start, end };
  }
  const start = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate(), 0, 0, 0);
  const end = new Date(centerDate.getFullYear(), centerDate.getMonth(), centerDate.getDate(), 23, 59, 59);
  return { start, end };
}
